import { draw, getNumNonClickable, newGame, reset, setLevel } from ".";
import { getGameEventParams, logEvent } from "../analytics";
import { CELL_STATE, INVERSE_CELL_STATE, setUse } from "../cell";
import { COMPLIMENT_TYPE, launchComplimentAnimation } from "../compliment";
import { RUN_EFFECTS_PARALLEL } from "../config";
import { XLINKS, XMLNS } from "../constants";
import { boardEl, showTipBtnEl, stepsEl } from "../elements";
import { launchWinAnim, positionObject } from "../levels";
import { incrementLives, loseLife } from "../lives";
import { incrementPowerUserCount } from "../pgs/achievements";
import "../ripple";
import { showSuccessSnackbar } from "../snackbar";
import { playMp3 } from "../sound";
import store from "../store";
import {
  setSecondsPassed,
  startTimerInterval,
  stopTimerInterval,
} from "../timer";
import { GAME_MODE, gameState, levelDef } from "./gameState";
import { center, catchCat, dropSeries, shuffle } from "./boosters";
import { stopToggleTipTimer } from "./helper";
import cachedFirestore from "../cachedFirestore";
import { increment } from "firebase/firestore";
import { stack } from "../nav";
import { updateStyles } from "../nav/styles";
import { showMap } from "../map";
import adManager from "../admob";
import { AdMob } from "../plugins";
import {
  Cat,
  CrossCode,
  CrossCode2,
  CrossCode3,
  CrossCode4,
  PixelCat,
  resetSprites,
} from "./cat";
import { randomChoice } from "../utils";
import { getLogger } from "../logging";

const log = getLogger("🐈‍ Cats");

let i_ = 0;
export const SKIN = {
  DEFAULT: i_++,
  RANDOM: i_++,
  PIXEL_CAT: i_++,
  CROSSCODE_CAT: i_++,
  CROSSCODE_FOX: i_++,
  CROSSCODE_PINGUIN: i_++,
  CROSSCODE_FATCAT: i_++,
  CROSSCODE_DINO: i_++,
  CROSSCODE_DOG: i_++,
};

function getCat(skin) {
  switch (skin) {
    case SKIN.DEFAULT:
      return new Cat();
    case SKIN.PIXEL_CAT:
      return new PixelCat();
    case SKIN.CROSSCODE_CAT:
      return new CrossCode();
    case SKIN.CROSSCODE_FOX:
      return new CrossCode("fox");
    case SKIN.CROSSCODE_PINGUIN:
      return new CrossCode4();
    case SKIN.CROSSCODE_FATCAT:
      return new CrossCode4("fatcat");
    case SKIN.CROSSCODE_DINO:
      return new CrossCode2();
    case SKIN.CROSSCODE_DOG:
      return new CrossCode3();
    case SKIN.RANDOM:
    default:
      return getCat(randomChoice(Object.values(SKIN)));
  }
}

export class Cats {
  #running;
  #losing;
  #cats;
  #lostCountPrev;
  #trappedCountPrev;

  constructor(catPlacement) {
    if (catPlacement) this.reset(catPlacement);
  }

  reset(catPlacement) {
    resetSprites();
    this.#losing = false;
    this.#running = false;
    this.lost = false;
    this.num = 0;
    this.#cats = [];
    this.#lostCountPrev = 0;
    this.#trappedCountPrev = 0;
    for (const [shape, shapeVariable] of catPlacement.shapes)
      positionObject(this._placeCat, levelDef.fieldSize, shape, shapeVariable);
    this.num = this.#cats.length;
    if (!this.num) log.warn("No cats!");
    log.debug(`Placed ${this.num} cat(s)`);
  }

  _placeCat = (cb) => {
    for (let i = 3; i < levelDef.fieldSize - 3; i++)
      for (let j = 3; j < levelDef.fieldSize - 3; j++)
        if (cb(i, j)) {
          if (
            [
              CELL_STATE.ERASE,
              CELL_STATE.OUTSIDE,
              CELL_STATE.NON_BLOCKABLE_OUTSIDE,
            ].includes(gameState.cells[i][j].state) ||
            gameState.cells[i][j].win
          ) {
            log.warn(
              `Tried placing cat on a invalid cell ${i},${j} ${
                INVERSE_CELL_STATE[gameState.cells[i][j].state]
              }`
            );
            return;
          } else if (gameState.cells[i][j].state == CELL_STATE.BLOCKED) {
            log.info(
              `Tried placing cat on a blocked cell ${i},${j}. Setting to non-blockable`
            );
            gameState.cells[i][j].state = CELL_STATE.NON_BLOCKABLE;
          }
          const cat = getCat(SKIN.DEFAULT);
          cat.init(j, i);
          this.#cats.push(cat);
        }
  };

  changeSkin(skin) {
    this.removeShapes();
    for (let i = 0; i < this.num; ++i) {
      const cat = this.#cats[i];
      const newCat = getCat(skin);
      newCat.assign(cat);
      this.#cats[i] = newCat;
      newCat.init(newCat.x, newCat.y);
    }
    this.appendShapes();
  }

  appendShapes() {
    const fragment = document.createDocumentFragment();
    const catsEl = document.createElementNS(XMLNS, "g");
    catsEl.setAttributeNS(null, "id", "cats");
    fragment.appendChild(catsEl);
    for (const cat of this.#cats) {
      if (cat.shape.parentNode) cat.shape.parentNode.removeChild(cat.shape);
      catsEl.appendChild(cat.shape);
    }
    boardEl.appendChild(fragment);
    this.sortShapes();
  }

  removeShapes() {
    for (const cat of this.#cats)
      if (cat.shape) cat.shape.parentNode.removeChild(cat.shape);
  }

  in(x, y) {
    for (const cat of this.#cats) {
      if (cat.x == x && cat.y == y) return true;
    }
    return false;
  }

  checkIfLostQuick = async () => {
    let loseCount = 0;
    const numNonClickable = getNumNonClickable();
    log.debug(`${numNonClickable} / ${levelDef.innerFieldSize}`);
    for (const cat of this.#cats) if (cat.hasWon()) return false;
    if (numNonClickable >= levelDef.innerFieldSize) {
      log.debug("All fields occupied");
      this.lost = true;
      return;
    }
    for (const cat of this.#cats) if (cat.hasLostQuick()) loseCount += 1;
    if (loseCount == this.num) {
      log.debug(`All ${this.num} cat(s) trapped (quick check)`);
      this.lost = true;
    }
  };

  runIfWon = async () => {
    if (this.won) await this.run();
    return this.won;
  };

  loseIfLost = async () => {
    if (this.lost) await this.lose();
    return this.lost;
  };

  isJumping = () => {
    for (const cat of this.#cats) if (cat.isJumping()) return true;
    return false;
  };

  async move() {
    let lostCount = 0;
    let trappedCount = 0;
    let excludeList = [];
    let drop_ = false;
    let center_ = false;
    let shuffle_ = false;
    let catch_ = false;
    let freeze_ = false;
    let taskList = [];
    const playPromises = [];
    for (const cat of this.#cats) {
      if (!cat.won) {
        const {
          promise: playPromise,
          catState: { lost, won, trapped },
        } = cat.play(excludeList);
        playPromises.push(playPromise);
        excludeList.push(cat.getCoordNum());
        if (lost) lostCount++;
        if (won) this.#setWon();
        if (trapped) trappedCount++;
      }
      const catCell = gameState.cells[cat.y][cat.x];
      if (catCell.state == CELL_STATE.SHUFFLE) shuffle_ = true;
      else if (catCell.state == CELL_STATE.DROP) drop_ = true;
      else if (catCell.state == CELL_STATE.CENTER) center_ = true;
      else if (catCell.state == CELL_STATE.CATCH) catch_ = true;
      else if (catCell.state == CELL_STATE.FREEZE) freeze_ = true;
      else if (catCell.state == CELL_STATE.RANDOM) {
        const randNum = Math.floor(Math.random() * 4);
        if (randNum == 0) shuffle_ = true;
        else if (randNum == 1) drop_ = true;
        else if (randNum == 2) center_ = true;
        else if (randNum == 3) catch_ = true;
        else if (randNum == 4) freeze_ = true;
        else throw new Error(`Should't happen ${randNum}`);
      }
      if (drop_ || center_ || shuffle_ || catch_ || freeze_) {
        if (center_ || catch_) taskList.push(["freeCell", freeCell, catCell]);
        else taskList.push(["nonBlockableCell", nonBlockableCell, catCell]);
        if (!this.won || center_)
          launchComplimentAnimation({
            duration: 2000,
            complimentType: COMPLIMENT_TYPE.BOOSTER,
          });
      }
      if (catCell.state == CELL_STATE.CATCH) {
        const otherCats = [];
        for (const otherCat of this.#cats) {
          if (otherCat.x != cat.x || otherCat.y != cat.y)
            otherCats.push(otherCat.getCoordNum());
        }
        taskList.push(["catchCat", catchCat, cat, otherCats]);
      }
    }
    this.lost = lostCount == this.num;
    this.#maybeLaunchWinAnimation(this.lost, lostCount, trappedCount);
    this.#lostCountPrev = lostCount;
    this.#trappedCountPrev = trappedCount;
    store.step++;
    if (store.step == 1) incrementPlayedCount();
    if (this.lost) {
      log.debug(`All ${this.num} cat(s) trapped`);
      showTipBtnEl.style.display = "none";
      stopToggleTipTimer();
      await this.runTasks(taskList);
      //   return this.runTasks(taskList).then(this.lose);
    } else {
      if (store.step == 0) startTimerInterval();
      else if (store.step == 3) incrementPowerUserCount();
      setSteps();
      setSecondsPassed();
      if (center_ && !hasTask(taskList, "center")) {
        taskList.push(["center", center]);
        this.#setWon(false);
        for (const cat of this.#cats) cat.won = false;
      }
      if (shuffle_ && !this.won && !hasTask(taskList, "shuffle"))
        taskList.push(["shuffle", shuffle]);
      if (drop_ && !this.won && !hasTask(taskList, "dropSeries"))
        taskList.push(["dropSeries", dropSeries]);
      await this.runTasks(taskList);
      if (gameState.mode != GAME_MODE.CLASSIC) await this.checkIfLostQuick();
      await Promise.all(playPromises);
      this.sortShapes();
      //   dispatchEvent(new Event("catsPlayed"));
    }
  }

  #setWon(won = true) {
    this.won = won;
    if (won) log.debug("Won");
  }

  #maybeLaunchWinAnimation = (lost, lostCount, trappedCount) => {
    if (
      (!lost && lostCount > this.#lostCountPrev && !this.won) ||
      (gameState.mode == GAME_MODE.CLASSIC &&
        trappedCount > this.#trappedCountPrev)
    ) {
      launchComplimentAnimation({
        duration: 2000,
        complimentType: COMPLIMENT_TYPE.ALL,
      });
      playMp3("meow2");
    }
  };

  lose = async () => {
    if (this.#losing) throw new Error("Already losing");
    this.#losing = true;
    if (levelDef.id && gameState.mode != GAME_MODE.EDITOR)
      store.set("levelsWon", store.levelsWon.add(levelDef.id));
    if (gameState.mode != GAME_MODE.EDITOR)
      logEvent("win", getGameEventParams());
    stopTimerInterval();
    showTipBtnEl.style.display = "none";
    stopToggleTipTimer();
    const wasPlayingLatestLevel = store.levelIdx == store.maxLevelIdx;
    if (gameState.mode == GAME_MODE.STORY) setLevel();
    reset();
    if (
      gameState.mode == GAME_MODE.STORY &&
      wasPlayingLatestLevel &&
      store.numLives < 9
    )
      setTimeout(earnLifeForWinning, 450);
    incrementWonCount();
    const shortWinAnim = gameState.mode == GAME_MODE.EDITOR;
    await launchWinAnim(shortWinAnim);
    await onCatsTrapped();
    await onGameEnded();
    this.#losing = false;
    // IS_WEB ||
    //   GoogleGameServices.submitEvent({
    //     eventId: WIN_LEVEL_EVENT_IDS[store.levelIdx],
    //   });
    // submitScore(
    //   Math.round(((store.levelIdx + 1) / (store.step + 1)) * 1000),
    //   store.levelIdx
    // );
    // unlockAchievement(store.levelIdx);
  };

  run = async () => {
    if (this.#running) throw new Error("Already running");
    this.#running = true;
    stopTimerInterval();
    const runCallbacks = [];
    for (const cat of this.#cats) if (cat.won) runCallbacks.push(cat.run());
    if (gameState.mode == GAME_MODE.STORY) loseLife();
    logEvent("lose", getGameEventParams());
    incrementLostCount();
    // IS_WEB ||
    //   GoogleGameServices.submitEvent({
    //     eventId: LOOSE_LEVEL_EVENT_IDS[store.levelIdx],
    //   });
    playMp3("fast-chop");

    await Promise.all(runCallbacks);
    await reset();
    onCatsEscaped();
    onGameEnded();
    this.#running = false;
  };

  displayRandom() {
    for (const cat of this.#cats) {
      const dir = randomChoice([0, 1, 2, 3, 4, 5]);
      cat.display(cat.px, cat.py, dir);
    }
  }

  resetDisplay() {
    for (const cat of this.#cats) {
      cat.display(0, 0);
    }
  }

  get(i) {
    if (i >= this.#cats.length) throw Error("Invalid Index");
    return this.#cats[i];
  }

  getAll() {
    return this.#cats;
  }

  runTasks(taskList) {
    if (!taskList.length) {
      const p = Promise.resolve();
      return p;
    }
    let p;
    if (RUN_EFFECTS_PARALLEL) {
      p = Promise.all(
        taskList.map(([taskName, cb, ...args]) => {
          const p = cb(...args);
          if (typeof p?.then === "function")
            return p.catch((e) =>
              log.error(`Parallel task "${taskName}" finished with errors ${e}`)
            );
        })
      ).finally(() => {
        // TODO: remove anonymous function
        log.debug(
          `Parallel tasks ${taskList.map(([taskName]) => taskName)} finished`
        );
        for (const cat of this.#cats) {
          const catCell = gameState.cells[cat.y][cat.x];
          if (catCell.state == CELL_STATE.BLOCKED) {
            catCell.state = CELL_STATE.NON_BLOCKABLE;
          }
          setUse(catCell.id, catCell.state);
        }
        draw();
        this.appendShapes();
      });
    } else {
      p = Promise.resolve();
      log.debug(taskList);
      for (const [i, task] of taskList.entries()) {
        const [taskName, cb] = task;
        p = p
          .then(() =>
            log.debug(`Running task ${i + 1}/${taskList.length} "${taskName}"`)
          )
          .then(cb);
      }
    }
    p = p.catch((e) => log.warn(`Tasks finished with errors ${e}`));
    return p;
  }

  getAvgY() {
    let sum = 0;
    for (const cat of this.#cats) sum += cat.y;
    return sum / this.num;
  }

  copy() {
    const copy = new Cats();
    copy.lost = this.lost;
    copy.num = this.num;
    copy.#cats = this.#cats.map((cat) => cat.copy());
    copy.#lostCountPrev = this.#lostCountPrev;
    copy.#trappedCountPrev = this.#trappedCountPrev;
    copy.#losing = this.#losing;
    copy.#running = this.#running;
    return copy;
  }

  sortShapes() {
    if (this.num < 2) return;
    const catsEl = boardEl.querySelector("#cats");
    catsEl.remove();
    const newCatsEl = document.createElementNS(XMLNS, "g");
    newCatsEl.setAttributeNS(null, "id", "cats");
    this.#cats.sort((a, b) => a.y - b.y);
    for (const cat of this.#cats) newCatsEl.appendChild(cat.shape);
    boardEl.appendChild(newCatsEl);
  }
}

function hasTask(taskList, taskName) {
  for (const [taskName_] of taskList) if (taskName == taskName_) return true;
  return false;
}

export function setSteps() {
  stepsEl.innerHTML =
    levelDef.stepsLimit == null ? store.step : levelDef.stepsLimit - store.step;
}

function freeCell(catCell) {
  if (
    catCell.state == CELL_STATE.OUTSIDE ||
    catCell.state == CELL_STATE.NON_BLOCKABLE ||
    catCell.state == CELL_STATE.NON_BLOCKABLE_OUTSIDE
  )
    return;
  catCell.state = CELL_STATE.FREE;
  catCell.id.setAttributeNS(XLINKS, "xlink:href", "#free-cell");
}

function nonBlockableCell(catCell) {
  if (
    catCell.state == CELL_STATE.OUTSIDE ||
    catCell.state == CELL_STATE.NON_BLOCKABLE_OUTSIDE
  )
    return;
  catCell.state = CELL_STATE.NON_BLOCKABLE;
  catCell.id.setAttributeNS(XLINKS, "xlink:href", "#non-blockable-cell");
  //   catCell.id.setAttributeNS(null, "fill", store.themes[7]);
}

function earnLifeForWinning() {
  incrementLives();
  showSuccessSnackbar("You earned a life for winning! 🎉", {
    duration: 3000,
  });
}

function onCatsTrapped() {
  log.debug("Cats trapped");
  dispatchEvent(new Event("catsTrapped"));
  if (gameState.mode == GAME_MODE.COMMUNITY) {
    stack.pop();
    updateStyles();
  }
}

function onCatsEscaped() {
  log.debug("Cats escaped");
  dispatchEvent(new Event("catsEscaped"));
  if ([GAME_MODE.COMMUNITY, GAME_MODE.CLASSIC].includes(gameState.mode))
    newGame({
      level: levelDef,
    });
}

function onGameEnded() {
  log.debug("Game ended");
  dispatchEvent(new Event("gameEnded"));
  if ([GAME_MODE.CLASSIC, GAME_MODE.COMMUNITY].includes(gameState.mode)) {
    // Show an ad if conditions are met
    if (
      !store.removedAds &&
      store.gameCount % store.interstitialAdsInterval === 0
    )
      adManager
        .showAd(AdMob, "Interstitial")
        .finally(() => adManager.prepareAd(AdMob, "Interstitial"));
  } else if (gameState.mode != GAME_MODE.EDITOR) showMap();
}

function incrementWonCount() {
  if (gameState.mode != GAME_MODE.COMMUNITY) return;
  cachedFirestore.updateDocument({
    reference: `communityLevels/${levelDef.id}`,
    data: {
      wonCount: increment(1),
    },
  });
}

function incrementLostCount() {
  if (gameState.mode != GAME_MODE.COMMUNITY) return;
  cachedFirestore.updateDocument({
    reference: `communityLevels/${levelDef.id}`,
    data: {
      lostCount: increment(1),
    },
  });
}

function incrementPlayedCount() {
  if (gameState.mode != GAME_MODE.COMMUNITY) return;
  cachedFirestore.updateDocument({
    reference: `communityLevels/${levelDef.id}`,
    data: {
      playedCount: increment(1),
    },
  });
}
